import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { GridRowParams } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import ReadOnlyAddress from '@/components/common/address_components/ReadOnlyAddress';
import { Spinner } from '@/components/common/Spinner';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridDetailsProps, ApplicationDataGridRowProps } from '@/models/Application';
import { ApplicationTypes } from '@/models/ApplicationType';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { getStatusDescription } from '@/utils/statusUtil';
import { ProgramNames } from '@/models/Program';

import ApplicationChangeLogModal from './ApplicationChangeLogModal';
import ApplicationDeleteModal from './ApplicationDeleteModal';
import ApplicationUpdateModal from './ApplicationUpdateModal/ApplicationUpdateModal';
import ManageAttachmentsModal from './ManageAttachmentsModal';
import { logError } from '@/utils/errorHandlerUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useApplicationTransaction from '@/hooks/useApplicationTransaction';
import { ProcessingStatusEnum } from '@/models/ApplicationTransaction';

function ApplicationDataGridDetails({
  applicationRow,
  updateRow,
}: {
  applicationRow: GridRowParams<ApplicationDataGridRowProps>;
  updateRow: (data: ApplicationDataGridRowProps) => void;
}) {
  const {
    getApplicationDataGridDetails,
    getPrintApplication,
    updateIsPendingApprovalAnnouncements,
  } = useApplication();
  const theme = useTheme();
  const { setAlert } = useContext(SnackbarContext);

  const { userType } = useContext(UsersContext);
  const { allStatuses } = useContext(ConfigContext);
  const { localeLanguageCode, translatedText } = useContext(LocaleContext);
  const { requeueApplicationTransaction, cancelApplicationTransaction } =
    useApplicationTransaction();
  const [downloading, setDownloading] = useState<boolean>(false);
  const [applicationDeleteModalOpen, setApplicationDeleteModalOpen] = useState<boolean>(false);
  const [applicationUpdateModalOpen, setApplicationUpdateModalOpen] = useState<boolean>(false);

  const [showChangeLogModal, setShowChangeLogModal] = useState<boolean>(false);
  const [showManageAttachmentsModal, setShowManageAttachmentsModal] = useState<boolean>(false);

  const applicationId = useMemo(() => applicationRow.row.applicationId, [applicationRow]);

  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationDataGridDetailsProps | null>(null);

  const approvedByDisplay = useMemo(() => {
    if (
      selectedApplication != null &&
      selectedApplication.status === ApplicationStatus.ApplicationApproved &&
      selectedApplication.approvedByUser?.contact != null
    ) {
      return (
        selectedApplication.approvedByUser.contact.firstName +
        ' ' +
        selectedApplication.approvedByUser.contact.lastName +
        ' (' +
        selectedApplication.approvedByUser.contact.email +
        ')'
      );
    } else {
      return null;
    }
  }, [selectedApplication]);

  const showReadyForApprovalAnnouncementCheckBox = useMemo(() => {
    return (
      selectedApplication != null &&
      selectedApplication.status === ApplicationStatus.PendingAdfReview &&
      selectedApplication.programId === ProgramNames.TreeCitiesWorld &&
      userType === UserType.Adf
    );
  }, [selectedApplication, userType]);

  const showAdditionalStickers = useMemo(() => {
    return (
      selectedApplication != null &&
      selectedApplication.programId === ProgramNames.TreeCityUSA &&
      selectedApplication.applicationTypeId === ApplicationTypes.Standard
    );
  }, [selectedApplication]);

  useEffect(() => {
    if (applicationId != null && selectedApplication == null) {
      const fetchData = async () => {
        const data = await getApplicationDataGridDetails(applicationId);
        const applicationData = {
          ...applicationRow.row,
          ...data,
        };
        setSelectedApplication(applicationData);
      };

      void fetchData();
    }
  }, [getApplicationDataGridDetails, applicationId, applicationRow, selectedApplication]);

  const handleDownloadApplication = async () => {
    setDownloading(true);

    await getPrintApplication(applicationId);
    setDownloading(false);
  };

  const handleDeleteApplication = () => {
    setApplicationDeleteModalOpen(true);
  };

  const handleApplicationUpdate = useCallback(
    (applicationDetails: ApplicationDataGridDetailsProps) => {
      if (applicationDetails != null) {
        setSelectedApplication(applicationDetails);
        updateRow(applicationDetails);
      }
    },
    [updateRow]
  );

  const requeueApplicationForProcessing = useCallback(
    async (transactionId: number) => {
      try {
        await requeueApplicationTransaction(transactionId);

        const updatedTransactions =
          selectedApplication?.failedApplicationTransactions?.filter(
            (t) => t.id !== transactionId
          ) ?? null;

        updateRow({ ...applicationRow.row, failedApplicationTransactions: updatedTransactions });

        setAlert({
          severity: 'success',
          message: 'Application requeued for processing.',
        });
      } catch (e) {
        logError(
          `Error requeueing application ${applicationId} for Business Central processing.`,
          e
        );
        setAlert({
          severity: 'error',
          message: 'Error requeueing application for processing - please try again.',
        });
      }
    },
    [
      applicationId,
      applicationRow.row,
      requeueApplicationTransaction,
      selectedApplication?.failedApplicationTransactions,
      setAlert,
      updateRow,
    ]
  );

  const cancelApplicationProcessing = useCallback(
    async (transactionId: number) => {
      try {
        await cancelApplicationTransaction(transactionId);

        const updatedTransactions =
          selectedApplication?.failedApplicationTransactions?.filter(
            (t) => t.id !== transactionId
          ) ?? null;

        updateRow({ ...applicationRow.row, failedApplicationTransactions: updatedTransactions });

        setAlert({
          severity: 'success',
          message: 'Application transaction cancelled successfully.',
        });
      } catch (e) {
        logError(
          `Error canceling application transaction ${applicationId} for Business Central processing.`,
          e
        );
        setAlert({
          severity: 'error',
          message: 'Error canceling application transaction - please try again.',
        });
      }
    },
    [
      applicationId,
      applicationRow.row,
      cancelApplicationTransaction,
      selectedApplication?.failedApplicationTransactions,
      setAlert,
      updateRow,
    ]
  );

  const handleAnnouncementApprovalCheckboxChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;

      try {
        await updateIsPendingApprovalAnnouncements(applicationId, checked).then(
          (updatedStatus: ApplicationStatus) => {
            if (selectedApplication != null && updatedStatus !== null) {
              const updatedApp = {
                ...selectedApplication,
                isPendingApprovalAnnouncement: checked,
                statusDescription: checked
                  ? 'Pending Approval Announcement'
                  : (getStatusDescription(allStatuses, updatedStatus, localeLanguageCode) ?? ''),
              };

              setSelectedApplication(updatedApp);
              updateRow(updatedApp);
            }
          }
        );
      } catch (e) {
        logError(
          `Error updating announcement approval status for application ${applicationId}.`,
          e
        );
        setAlert({
          severity: 'error',
          message: 'Error updating announcement approval status - please try again.',
        });

        return e;
      }

      return null;
    },
    [
      allStatuses,
      applicationId,
      localeLanguageCode,
      selectedApplication,
      setAlert,
      updateIsPendingApprovalAnnouncements,
      updateRow,
    ]
  );

  const hasEditPermission: boolean = useMemo(() => {
    return (
      selectedApplication != null &&
      userType != null &&
      (UserType.Adf === userType ||
        (userType == UserType.StateCoordinator &&
          selectedApplication.applicationTypeId === ApplicationTypes.StateQuestions))
    );
  }, [selectedApplication, userType]);

  const shippingContactAddress = useMemo(() => {
    if (selectedApplication?.shippingContact == null) return null;

    return {
      streetAddress1: selectedApplication?.shippingContact?.streetAddress1,
      streetAddress2: selectedApplication?.shippingContact?.streetAddress2,
      city: selectedApplication?.shippingContact?.city,
      stateId: selectedApplication?.shippingContact?.stateId,
      countryId: selectedApplication?.shippingContact?.countryId,
      countyId: selectedApplication?.shippingContact?.countyId,
      zipCode: selectedApplication?.shippingContact?.zipCode,
    };
  }, [selectedApplication]);

  return selectedApplication != null ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        borderBottom: `1px solid ${theme.palette.sandShade2.main}`,
        height: 'calc(100% - 24px)',
        overflowY: 'auto',
        padding: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${theme.palette.sandShade2.main}`,
        }}
      >
        {hasEditPermission && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              setApplicationUpdateModalOpen(true);
            }}
          >
            Edit
          </Button>
        )}

        <Button
          disabled={downloading}
          variant='secondary'
          size='small'
          onClick={() => {
            void handleDownloadApplication();
          }}
        >
          {translatedText.dashboard.download}
        </Button>

        {userType === UserType.Adf && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              void handleDeleteApplication();
            }}
            disabled={
              selectedApplication == null ||
              userType != UserType.Adf ||
              !selectedApplication.applicationCycleActive ||
              ![1, 7].includes(selectedApplication.status)
            }
          >
            Delete
          </Button>
        )}

        {userType === UserType.Adf && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              setShowChangeLogModal(true);
            }}
          >
            Change Log
          </Button>
        )}

        {userType === UserType.Adf && (
          <Button
            variant='secondary'
            size='small'
            onClick={() => {
              setShowManageAttachmentsModal(true);
            }}
          >
            Manage Attachments
          </Button>
        )}
      </Box>

      {selectedApplication.failedApplicationTransactions != null &&
        selectedApplication.failedApplicationTransactions.length > 0 && (
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.sandShade2.main}`,
              paddingBottom: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography variant='overline2'>Application Transactions</Typography>
            {selectedApplication.failedApplicationTransactions.map((transaction) => (
              <Alert
                key={transaction.id}
                severity={
                  transaction.processingStatusId === ProcessingStatusEnum.Failed
                    ? 'error'
                    : 'warning'
                }
                sx={{
                  width: '90vw',
                  '& .MuiAlert-message': { width: '100%' },
                  '& .MuiAlert-icon': { alignItems: 'center' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 0.5,
                      alignItems: 'center',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Tooltip title='Requeue this transaction for processing.'>
                      <IconButton
                        onClick={() => void requeueApplicationForProcessing(transaction.id)}
                        disabled={transaction.processingStatusId !== ProcessingStatusEnum.Failed}
                        size='small'
                        sx={{
                          color: theme.palette.oceanShade.main,
                        }}
                      >
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>

                    <Typography
                      color='textSecondary'
                      sx={{ whiteSpace: 'nowrap' }}
                    >{`Transaction ID: ${transaction.id}`}</Typography>
                    <Typography
                      color='textSecondary'
                      flex={2}
                      sx={{ wordBreak: 'break-word', minWidth: 0 }}
                    >
                      {transaction.errorMessage}
                    </Typography>
                  </Box>

                  {transaction.processingStatusId !== ProcessingStatusEnum.Cancelled ? (
                    <Tooltip title='Cancel this transaction'>
                      <IconButton
                        onClick={() => void cancelApplicationProcessing(transaction.id)}
                        sx={{
                          color: theme.palette.error.main,
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography
                      color={theme.palette.text.primary}
                      sx={{ fontWeight: 'bold', alignContent: 'center' }}
                    >
                      Cancelled
                    </Typography>
                  )}
                </Box>
              </Alert>
            ))}
          </Box>
        )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '64px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box>
            <Typography variant='overline2'>Application Deadline</Typography>
            <Typography color='textSecondary'>
              {selectedApplication.applicationDeadline != null
                ? dayjs(selectedApplication.applicationDeadline.split('T')[0]).format('LL')
                : '---'}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Start Date</Typography>
            <Typography color='textSecondary'>
              {selectedApplication.startDate != null
                ? dayjs(selectedApplication.startDate.split('T')[0]).format('LL')
                : '---'}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Last Updated</Typography>
            <Typography color='textSecondary'>
              {selectedApplication.lastUpdatedDate != null
                ? dayjs(selectedApplication.lastUpdatedDate.split('T')[0]).format('LL')
                : '---'}
            </Typography>
          </Box>
        </Box>

        {(selectedApplication.status === ApplicationStatus.Closed ||
          approvedByDisplay != null ||
          showReadyForApprovalAnnouncementCheckBox) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {selectedApplication.status === ApplicationStatus.Closed && (
              <Box>
                <Typography variant='overline2'>Last Status</Typography>
                <Typography color='textSecondary'>
                  {selectedApplication.lastStatusDescription ?? '---'}
                </Typography>
              </Box>
            )}

            {approvedByDisplay != null && (
              <Box>
                <Typography variant='overline2'>Approved By</Typography>
                <Typography color='textSecondary'>{approvedByDisplay}</Typography>
              </Box>
            )}

            {showReadyForApprovalAnnouncementCheckBox && (
              <Box>
                <FormControlLabel
                  label='Pending Approval Announcement'
                  control={
                    <Checkbox
                      id={`ready-for-approval-${selectedApplication.applicationId}`}
                      checked={selectedApplication.isPendingApprovalAnnouncement}
                      onChange={handleAnnouncementApprovalCheckboxChange}
                    />
                  }
                />
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {showAdditionalStickers && (
            <Box>
              <Typography variant='overline2'>Additional Stickers</Typography>
              <Typography color='textSecondary'>
                {selectedApplication.additionalStickers ?? '---'}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant='overline2'>Special Instructions</Typography>
            <Typography color='textSecondary'>
              {selectedApplication.specialInstructions ?? '---'}
            </Typography>
          </Box>

          {userType === UserType.Adf && selectedApplication.businessCentralSalesOrderId != null && (
            <Box>
              <Typography variant='overline2'>Materials - Sales Order</Typography>
              <Typography color='textSecondary'>
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`${import.meta.env.VITE_BUSINESS_CENTRAL_URL}?company=Arbor%20Day%20Foundation&page=42&filter=%27Sales%20Header%27.%27No.%27%20IS%20%27${selectedApplication.businessCentralSalesOrderId}%27`}
                >
                  {selectedApplication.businessCentralSalesOrderId}
                </a>
              </Typography>
            </Box>
          )}

          {userType === UserType.Adf &&
            selectedApplication.feeBusinessCentralSalesOrderId != null && (
              <Box>
                <Typography variant='overline2'>Payment - Sales Order</Typography>
                <Typography color='textSecondary'>
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    href={`${import.meta.env.VITE_BUSINESS_CENTRAL_URL}?company=Arbor%20Day%20Foundation&page=42&filter=%27Sales%20Header%27.%27No.%27%20IS%20%27${selectedApplication.feeBusinessCentralSalesOrderId}%27`}
                  >
                    {selectedApplication.feeBusinessCentralSalesOrderId}
                  </a>
                </Typography>
              </Box>
            )}
        </Box>

        {shippingContactAddress != null && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box>
              <Typography variant='overline2'>Selected Shipping Contact</Typography>

              <Typography color='textSecondary'>
                {`${selectedApplication.shippingContact?.firstName} ${selectedApplication.shippingContact?.lastName}`}{' '}
                {selectedApplication.shippingContact?.email != null
                  ? `(${selectedApplication.shippingContact?.email})`
                  : ''}
              </Typography>
            </Box>

            <ReadOnlyAddress
              address={shippingContactAddress}
              programId={selectedApplication.programId}
            />
          </Box>
        )}
      </Box>

      {applicationUpdateModalOpen && (
        <ApplicationUpdateModal
          applicationUpdateModalOpen={applicationUpdateModalOpen}
          applicationDataRow={selectedApplication}
          theme={theme}
          onModalClose={() => {
            setApplicationUpdateModalOpen(false);
          }}
          handleApplicationUpdate={handleApplicationUpdate}
        />
      )}

      {applicationDeleteModalOpen && (
        <ApplicationDeleteModal
          applicationDeleteModalOpen={applicationDeleteModalOpen}
          setApplicationDeleteModalOpen={setApplicationDeleteModalOpen}
          modalApplication={selectedApplication}
          theme={theme}
        />
      )}

      {showChangeLogModal && (
        <ApplicationChangeLogModal
          showModal={showChangeLogModal}
          setShowModal={setShowChangeLogModal}
          modalApplication={selectedApplication}
          theme={theme}
        />
      )}
      {showManageAttachmentsModal && (
        <ManageAttachmentsModal
          applicationId={selectedApplication.applicationId}
          onClose={() => setShowManageAttachmentsModal(false)}
        />
      )}
    </Box>
  ) : (
    <Box display={'flex'} mt={5} alignContent={'center'} justifyContent={'center'}>
      <Spinner size={100} type='circular' />
    </Box>
  );
}

export default ApplicationDataGridDetails;
